/**
 * What the admin sees when deleting a user is refused (SC-439). The server refuses to delete the
 * last active Owner of a company (409 `last_owner`) and says why; other failures keep the generic
 * text, since their bodies are not meant for display.
 */
import { AdminHttpError } from '../../services/AdminService'

export const LAST_OWNER_CODE = 'last_owner'
const LAST_OWNER_FALLBACK =
  'This user is the last active Owner of a company. Make someone else an Owner first.'

export const GENERIC_DELETE_FAILURE = 'Failed to delete user. Please try again.'

/** The server's reason when the account is a company's last Owner, else null. */
export function lastOwnerReason(error: unknown): string | null {
  if (error instanceof AdminHttpError && error.status === 409 && error.code === LAST_OWNER_CODE) {
    return error.serverMessage ?? LAST_OWNER_FALLBACK
  }
  return null
}

export function describeSingleDeleteFailure(error: unknown): string {
  return lastOwnerReason(error) ?? GENERIC_DELETE_FAILURE
}

export interface BulkDeleteOutcome {
  userId: string
  name: string
  status: 'deleted' | 'refused_last_owner' | 'failed'
  /** The server's sentence for a last-Owner refusal. */
  reason?: string
}

export function bulkOutcomeFromResult(
  userId: string,
  name: string,
  result: { success?: boolean } | undefined
): BulkDeleteOutcome {
  return { userId, name, status: result?.success ? 'deleted' : 'failed' }
}

export function bulkOutcomeFromError(
  userId: string,
  name: string,
  error: unknown
): BulkDeleteOutcome {
  const reason = lastOwnerReason(error)
  return reason
    ? { userId, name, status: 'refused_last_owner', reason }
    : { userId, name, status: 'failed' }
}

/**
 * Deletes the users one at a time. The server checks "last active Owner" against the state it sees
 * when each call arrives, so two calls in flight at once would each still see the other Owner and
 * could remove both Owners of a company.
 */
export async function deleteUsersSequentially(
  users: readonly { userId: string; name: string }[],
  deleteOne: (userId: string) => Promise<{ success?: boolean } | undefined>
): Promise<BulkDeleteOutcome[]> {
  const outcomes: BulkDeleteOutcome[] = []
  for (const { userId, name } of users) {
    try {
      outcomes.push(bulkOutcomeFromResult(userId, name, await deleteOne(userId)))
    } catch (error) {
      console.error('Error deleting user:', error)
      outcomes.push(bulkOutcomeFromError(userId, name, error))
    }
  }
  return outcomes
}

/**
 * The message after a bulk delete, or null when every user was deleted. Users refused as last
 * Owner are named with the server's reason; other failures keep the count-only wording.
 */
export function summarizeBulkDelete(outcomes: readonly BulkDeleteOutcome[]): string | null {
  const refused = outcomes.filter((o) => o.status === 'refused_last_owner')
  const failed = outcomes.filter((o) => o.status === 'failed')
  if (refused.length === 0 && failed.length === 0) return null

  const lines: string[] = []
  if (failed.length > 0) {
    lines.push(
      `Failed to delete ${failed.length} out of ${outcomes.length} users. Please try again.`
    )
  }
  if (refused.length > 0) {
    lines.push(
      `${refused.length === 1 ? '1 user was' : `${refused.length} users were`} not deleted because ${
        refused.length === 1 ? 'it is' : 'they are'
      } the last active Owner of a company:`
    )
    for (const o of refused) lines.push(`- ${o.name}: ${o.reason}`)
  }
  return lines.join('\n')
}
